import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ExportButton, type ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { trackAnalyticsExport } from '@/modules/analytics/utils/analyticsTracking';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { PageName } from '@/shared/types/segment';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import { useAiAgents } from './hooks/useAiAgents';

/**
 * Export button for the AI Agents analytics page.
 *
 * Mirrors the behavior of `OverviewGlobalExport`.
 *
 * TODO(pre-launch): when the table flips into single-agent / versions mode
 * this still exports the agents list, not the visible versions. Pass the
 * active scope before launch.
 */
export const AiAgentsExport = () => {
  const { t } = useTranslation();
  const { data: agents } = useAiAgents();
  const [isExporting, setIsExporting] = useState(false);

  const exportAll = useCallback(
    (format: ExportFormat) => {
      try {
        setIsExporting(true);
        trackAnalyticsExport(PageName.ANALYTICS_AI_AGENTS, 'ai_agents', format);
        const filename = buildExportFilename('ai_agents');
        const rows = agents.map(({ id, name, current }) => ({
          id,
          name,
          csat: current.csat,
          containment: current.containment,
          response_time: current.responseTime,
          total_conversations: current.totalConversations,
        }));

        if (format === 'json') {
          downloadJson({ ai_agents: rows }, filename);
        } else {
          downloadCsv(rows, filename);
        }
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [agents, t]
  );

  return <ExportButton variant="outline" onExport={exportAll} isExporting={isExporting} />;
};
